import { alpha, Box, styled, SxProps, Theme } from '@mui/material';

export const Toolbar = styled(Box, { name: 'PolicyToolbar' })({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  gap: '0.125rem',
  minWidth: 0,
  padding: '0 0.25rem 0.5625rem',
});

const focusVisibleSx = (theme: Theme) => ({
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.custom.OV.sys.primary}`,
    outlineOffset: '1px',
  },
});

export const ScopeButton = styled('button', { name: 'PolicyScopeButton' })(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.25rem',
  flex: '1 1 auto',
  minWidth: 0,
  background: 'transparent',
  border: 0,
  borderRadius: '0.3125rem',
  padding: '0.1875rem 0.3125rem',
  marginLeft: '-0.3125rem',
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '-0.005em',
  fontFamily: 'inherit',
  color: theme.palette.custom.OV.sys.onBackground,
  textAlign: 'left',
  cursor: 'pointer',

  '&:hover, &[aria-expanded="true"]': {
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
  },

  ...focusVisibleSx(theme),
}));

export const ScopeHeading = styled('span', { name: 'PolicyScopeHeading' })(({ theme }) => ({
  display: 'block',
  flex: '1 1 auto',
  minWidth: 0,
  padding: '0.1875rem 0.3125rem',
  marginLeft: '-0.3125rem',
  fontSize: '0.75rem',
  fontWeight: 600,
  letterSpacing: '-0.005em',
  color: theme.palette.custom.OV.sys.onBackground,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
}));

export const ScopeLabel = styled('span', { name: 'PolicyScopeLabel' })({
  flex: '1 1 auto',
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
});

export const FilterChip = styled('button', {
  name: 'PolicyFilterChip',
  shouldForwardProp: (prop) => prop !== 'lit',
})<{ lit?: boolean }>(({ theme, lit }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.3125rem',
  flexShrink: 0,
  background: 'transparent',
  border: 0,
  borderRadius: '0.375rem',
  padding: '0.25rem 0.375rem 0.25rem 0.4375rem',
  fontSize: '0.71875rem',
  fontWeight: 500,
  fontFamily: 'inherit',
  color: theme.palette.custom.OV.sys.onSurfaceVariant,
  whiteSpace: 'nowrap',
  cursor: 'pointer',

  '&:hover, &[aria-expanded="true"]': {
    backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
    color: theme.palette.custom.OV.sys.onBackground,
  },

  ...(lit && {
    color: theme.palette.custom.OV.sys.secondary,
    backgroundColor: alpha(theme.palette.custom.OV.sys.secondary, 0.13),

    '&:hover, &[aria-expanded="true"]': {
      backgroundColor: alpha(theme.palette.custom.OV.sys.secondary, 0.2),
    },
  }),

  ...focusVisibleSx(theme),
}));

export const FilterChipCount = styled('span', { name: 'PolicyFilterChipCount' })({
  flexShrink: 0,
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
});

export const filterChipIconSx: SxProps<Theme> = {
  display: 'grid',
  placeItems: 'center',
  flexShrink: 0,
};

export const chevronSx = (open: boolean): SxProps<Theme> => ({
  display: 'grid',
  placeItems: 'center',
  opacity: 0.8,
  flexShrink: 0,
  transform: open ? 'rotate(180deg)' : 'none',
});

export const OverflowButton = styled('button', { name: 'PolicyOverflowButton' })(
  ({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: '1.625rem',
    height: '1.625rem',
    background: 'transparent',
    border: 0,
    borderRadius: '0.375rem',
    color: theme.palette.custom.OV.sys.onSurfaceVariant,
    cursor: 'pointer',

    '&:hover, &[aria-expanded="true"]': {
      backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
      color: theme.palette.custom.OV.sys.onBackground,
    },

    ...focusVisibleSx(theme),
  }),
);
